import { Button, Col, Descriptions, Flex, Input, Row, Switch } from "antd";
import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CollapseArrayView from "../../../../components/CollapseArrayView/CollapseArrayView";
import { ModalsContext } from "../../../../context/modals.context";
import { UserRoles } from "../../../../modules/auth/auth.constants";
import { AuthContext } from "../../../../modules/auth/auth.context";
import {
  useEditCompanyEmail,
  useToggleNotificationStatus,
} from "../../../../modules/settings/settings.queries";
import { CompanyDetails } from "../../../../modules/settings/settings.types";
import { ApiResponseObject } from "../../../../types";
import { toastService } from "../../../../utils/toast.service";
import style from "./CompanyDetails.module.scss";

interface Props {
  companyDetails: CompanyDetails | undefined;
}

const CompanyContactSection = ({ companyDetails }: Props) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const { setError } = useContext(ModalsContext);
  const [newEmail, setNewEmail] = useState("");

  const { mutate: editEmail, isPending: isEditEmailPending } =
    useEditCompanyEmail({
      onError: onErrorHandler,
      onSettled: editEmailSettledHandler,
    });

  const {
    mutate: toggleEmailNotification,
    isPending: isEmailNotificationPending,
    data: emailNotificationData,
  } = useToggleNotificationStatus({
    onError: onErrorHandler,
    onSettled: toggleNotificationSettledHandler,
  });

  const hasEditRole =
    auth.allUserRoles?.includes(UserRoles.MfinKonfiguracija) ||
    auth.allUserRoles?.includes(UserRoles.MfinKorisnici) ||
    auth.allUserRoles?.includes(UserRoles.Mfin) ||
    auth.allUserRoles?.includes(UserRoles.Obveznik) ||
    auth.allUserRoles?.includes(UserRoles.ObveznikKonfiguracija);

  const descriptionItems = useMemo(
    () => [
      {
        key: "telefon",
        label: t("SETTINGS_COMPANY.TELEFON"),
        children: (
          <Col>
            {companyDetails?.kontaktTelefoni
              ?.slice(0, 3)
              ?.map((racun, index) => (
                <Row key={index}>{racun}</Row>
              ))}
            {companyDetails?.kontaktTelefoni?.slice(3)?.length ? (
              <CollapseArrayView
                elementKey="kontaktTelefoni"
                items={companyDetails?.kontaktTelefoni}
              />
            ) : (
              <></>
            )}
          </Col>
        ),
      },
      {
        key: "notificationAddress",
        label: t("SETTINGS_API.ADRESA_ZA_NOTIFIKACIJE"),
        children: (
          <Flex gap={10}>
            <Input
              value={newEmail}
              onChange={onEmailChangeHandler}
              readOnly={!hasEditRole}
            />
            {hasEditRole && (
              <Button
                onClick={() => editEmail(newEmail)}
                loading={isEditEmailPending}
                type="primary"
              >
                {t("COMMON.SACUVAJ")}{" "}
              </Button>
            )}
          </Flex>
        ),
      },
      {
        key: "notifications",
        label: t("SETTINGS_API.NOTIFIKACIJE"),
        children: (
          <Switch
            checked={
              emailNotificationData?.data !== undefined
                ? Boolean(emailNotificationData.data)
                : Boolean(companyDetails?.iskljucenaNotifikacija)
            }
            loading={isEmailNotificationPending}
            onChange={(value: boolean) => toggleEmailNotification(value)}
            disabled={!hasEditRole}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      newEmail,
      hasEditRole,
      isEditEmailPending,
      companyDetails?.kontaktTelefoni,
      companyDetails?.iskljucenaNotifikacija,
      isEmailNotificationPending,
      emailNotificationData,
    ]
  );

  function onErrorHandler() {
    setError(t("COMMON.DOSLO_JE_DO_GRESKE"));
  }

  function editEmailSettledHandler(
    data: ApiResponseObject<string> | undefined
  ) {
    if (!data) {
      onErrorHandler();
      return;
    }

    if (!data.success) {
      setError(data.errorMessage);
      return;
    }
  }

  function toggleNotificationSettledHandler(
    data: ApiResponseObject<boolean> | undefined
  ) {
    if (!data) {
      onErrorHandler();
      return;
    }

    if (!data.success) {
      setError(data.errorMessage);
      return;
    }

    if (data?.data) {
      toastService.success(t("TOAST.NOTIF_OFF_SUCCESS"));
    } else {
      toastService.success(t("TOAST.NOTIF_ON_SUCCESS"));
    }
  }

  function onEmailChangeHandler(e: ChangeEvent<HTMLInputElement>) {
    setNewEmail(e.target.value);
  }

  useEffect(() => {
    if (companyDetails?.emailForStatusDoc && !newEmail) {
      setNewEmail(companyDetails?.emailForStatusDoc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDetails?.emailForStatusDoc]);

  return (
    <Descriptions
      column={1}
      title={t("SETTINGS_COMPANY.KONTAKT")}
      className={style.descriptionsWrapper}
      labelStyle={{ width: "50%" }}
      items={descriptionItems}
      colon={false}
    />
  );
};
export default CompanyContactSection;
